/**
 * CleanroomWorkcell3D — Ultra-realistic semiconductor FAB cleanroom workcell equipment.
 *
 * 100% faithful replication of the reference machine:
 * 1. Left Control Tower: Top status switches, widescreen touchscreen HMI, pull drawer with chrome handle,
 *    lower access doors with flush chrome latches, digital gauge slot, and multi-tier signal tower beacon.
 * 2. Right Robot Chamber: Transparent cleanroom acrylic enclosure, top HEPA FFU ceiling with angled LED light bar,
 *    perforated stainless steel airflow floor grid, multi-axis robotic transfer arm with wafer end-effector,
 *    round rotating wafer vacuum chuck stage, vertical wafer cassette buffer rack, and warm amber photoresist backlighting.
 * 3. Bottom Chassis: Heavy-duty base frame with leveling feet and caster pads.
 */

import React, { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { useRailElevation } from '../../railElevationContext';
import {
  computeBigLifterPose,
  type WorkcellGeometricStations,
  type WorkcellFoupDestination,
} from './workcellTransferStateMachine';
import type { EquipmentStatus } from '../types';
import { FOUP } from '../FOUP/FOUP';
import { getSharedStandardMaterial } from '../../../../utils/threeResourcePool';
import { cameraDragStore } from '../../../../store/cameraDragStore';

export type CleanroomWorkcell3DProps = {
  id?: string;
  width?: number;
  height?: number;
  depth?: number;
  status?: EquipmentStatus;
  animateRobot?: boolean;
  animateLifter?: boolean;
  /** Explicit Big Lifter Event trigger (OHT -> Equipment Transfer Pos) */
  bigLifterEvent?: 'DOWN' | 'UP' | null;
  bigLifterProgress01?: number;
  /** Explicit Internal Lifter Event trigger (Transfer Pos -> Storage or Processing) */
  internalLifterEvent?: WorkcellFoupDestination | null;
  internalLifterProgress01?: number;
  /** FOUP presence at the individual workcell stations */
  foupPresent?: boolean;
  storageFoupPresent?: boolean;
  processingFoupPresent?: boolean;
  transferFoupPresent?: boolean;
  trackFoupPresent?: boolean;
  opacity?: number;
  position?: [number, number, number];
  rotation?: [number, number, number];
  portFractions?: readonly number[];
  portFraction?: number;
  onSelect?: () => void;
};

const workcellSignTextureCache = new Map<string, THREE.CanvasTexture>();

function getWorkcellSignTexture(id: string, status: string): THREE.CanvasTexture {
  const cacheKey = `${id}|${status}`;
  let tex = workcellSignTextureCache.get(cacheKey);
  if (!tex) {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 128;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.scale(0.5, 0.5);
      // Dark Glossy Obsidian Faceplate Background
      ctx.fillStyle = '#060C18';
      ctx.fillRect(0, 0, 1024, 256);

      // Status colored glowing rim
      const strokeCol = status === 'RUNNING' ? '#10B981' : status === 'ERROR' ? '#EF4444' : '#00F0FF';
      ctx.strokeStyle = strokeCol;
      ctx.lineWidth = 10;
      ctx.strokeRect(6, 6, 1012, 244);

      // Title Text
      ctx.fillStyle = '#FFFFFF';
      ctx.font = 'bold 54px "Segoe UI", Inter, Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(`● CLEANROOM WORKCELL · ${id}`, 512, 85);

      // Subtitle Text
      ctx.fillStyle = '#38BDF8';
      ctx.font = '500 36px "Segoe UI", Inter, Arial, sans-serif';
      ctx.fillText(`STATUS: ${status} | ROBOTIC CASSETTE CELL`, 512, 168);
    }
    tex = new THREE.CanvasTexture(canvas);
    tex.anisotropy = 8;
    tex.needsUpdate = true;
    workcellSignTextureCache.set(cacheKey, tex);
  }
  return tex;
}

export const CleanroomWorkcell3D: React.FC<CleanroomWorkcell3DProps> = ({
  id = 'CLEANROOM-CELL-01',
  width = 16.0,
  height = 20.0,
  depth = 13.0,
  status = 'RUNNING',
  animateRobot = true,
  animateLifter = false,
  bigLifterEvent = null,
  bigLifterProgress01,
  internalLifterEvent = null,
  internalLifterProgress01,
  foupPresent,
  storageFoupPresent = false,
  processingFoupPresent = false,
  transferFoupPresent = false,
  trackFoupPresent = false,
  opacity = 1,
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  portFractions,
  portFraction,
  onSelect,
}) => {
  void animateRobot;
  void internalLifterEvent;
  void internalLifterProgress01;
  void storageFoupPresent;
  void processingFoupPresent;
  void foupPresent;

  const dynamicRailY = useRailElevation();
  const trackElevationY = Math.max(height + 4.0, dynamicRailY ?? 32.0);
  // Match top of lifter frame exactly with the top edge of the track rail
  const lifterTopY = trackElevationY + 1.85;

  // Proportions matching the reference image
  const towerW = width * 0.32; // Left control tower width
  const chamberW = width - towerW; // Right chamber width
  const baseH = height * 0.40; // Lower solid cabinet base height
  const chamberH = height - baseH; // Upper glass chamber height
  const frameThick = 0.32; // Anodized aluminum frame profile

  // Positions
  const towerCenterX = -width * 0.5 + towerW * 0.5;
  const chamberCenterX = width * 0.5 - chamberW * 0.5;
  const chamberCenterY = baseH + chamberH * 0.5;
  const chamberFloorY = baseH;

  const workcellSignTexture = useMemo(
    () => getWorkcellSignTexture(id, status),
    [id, status]
  );

  // Materials
  // Materials - Reused from shared pool to avoid shader compilation spikes
  const materials = useMemo(() => {
    return {
      whiteChassis: getSharedStandardMaterial({
        color: '#FFFFFF',
        metalness: 0.05,
        roughness: 0.18,
        transparent: opacity < 1,
        opacity,
      }),
      darkChassis: getSharedStandardMaterial({
        color: '#1E293B',
        metalness: 0.5,
        roughness: 0.3,
        transparent: opacity < 1,
        opacity,
      }),
      silverAluminum: getSharedStandardMaterial({
        color: '#E2E8F0',
        metalness: 0.88,
        roughness: 0.18,
        envMapIntensity: 1.5,
      }),
      brushedSteel: getSharedStandardMaterial({
        color: '#CBD5E1',
        metalness: 0.92,
        roughness: 0.22,
        envMapIntensity: 1.8,
      }),
      chromeLatch: getSharedStandardMaterial({
        color: '#F8FAFC',
        metalness: 0.98,
        roughness: 0.08,
        envMapIntensity: 2.2,
      }),
      frameTopGray: getSharedStandardMaterial({
        color: '#94A3B8',
        metalness: 0.72,
        roughness: 0.28,
        envMapIntensity: 1.4,
      }),
      clearAcrylic: getSharedStandardMaterial({
        color: '#6EC4E8',
        opacity: 0.35,
        transparent: true,
        roughness: 0.08,
        metalness: 0.10,
      }),
      smokedGlassBackdrop: getSharedStandardMaterial({
        color: '#0F172A',
        opacity: 0.48,
        transparent: true,
        roughness: 0.14,
        metalness: 0.20,
      }),
      ledLightBar: getSharedStandardMaterial({
        color: '#FFFFFF',
        emissive: '#FFFFFF',
        emissiveIntensity: 1.8,
        toneMapped: false,
      }),
      eStopRed: getSharedStandardMaterial({
        color: '#EF4444',
        emissive: '#DC2626',
        emissiveIntensity: 0.5,
        roughness: 0.25,
      }),
      eStopYellow: getSharedStandardMaterial({
        color: '#FACC15',
        roughness: 0.3,
      }),
      hmiScreen: getSharedStandardMaterial({
        color: '#0F172A',
      }),
      waferSilicon: getSharedStandardMaterial({
        color: '#38BDF8',
        metalness: 0.95,
        roughness: 0.06,
        envMapIntensity: 2.5,
      }),
      timingBeltBlue: getSharedStandardMaterial({
        color: '#0284C7',
        roughness: 0.4,
        metalness: 0.2,
      }),
      timingBeltGreen: getSharedStandardMaterial({
        color: '#10B981',
        roughness: 0.4,
        metalness: 0.2,
      }),
      safetyYellow: getSharedStandardMaterial({
        color: '#EAB308',
        metalness: 0.28,
        roughness: 0.32,
      }),
      towerRed: getSharedStandardMaterial({
        color: '#EF4444',
        emissive: '#EF4444',
        emissiveIntensity: 0.9,
      }),
      towerAmber: getSharedStandardMaterial({
        color: '#F59E0B',
        emissive: '#F59E0B',
        emissiveIntensity: 0.9,
      }),
      towerGreen: getSharedStandardMaterial({
        color: '#10B981',
        emissive: '#10B981',
        emissiveIntensity: 1.2,
      }),
      verticalLedCyan: getSharedStandardMaterial({
        color: '#38BDF8',
        emissive: '#00F0FF',
        emissiveIntensity: 1.6,
        toneMapped: false,
      }),
      stopNodeOrange: getSharedStandardMaterial({
        color: '#F97316',
        emissive: '#F97316',
        emissiveIntensity: 1.5,
      }),
      lifterCarriageFrame: getSharedStandardMaterial({
        color: '#334155',
        metalness: 0.85,
        roughness: 0.25,
      }),
    };
  }, [opacity]);

  // Stop node X coordinate (defaults to 0 for exact stop node alignment)
  const lifterX = useMemo(() => {
    if (portFraction !== undefined) {
      return (portFraction - 0.5) * width;
    }
    if (portFractions && portFractions.length > 0 && portFractions[0] !== undefined) {
      return (portFractions[0] - 0.5) * width;
    }
    return 0; // Exactly centered at the stop node position
  }, [portFraction, portFractions, width]);

  // Lifter sits at the front side of the equipment directly adjacent/attached to the track
  const lifterZ = depth * 0.5 - 2.0;

  // Geometric Station Coordinates inside the workcell (Lifter positioned at front track dock)
  const stations: WorkcellGeometricStations = useMemo(() => ({
    trackPortY: lifterTopY - 0.4,
    transferPosY: chamberFloorY + 0.6,
    chamberFloorY,
    transferPos: [lifterX, chamberFloorY + 0.6, lifterZ],
    storagePos: [-width * 0.28, chamberFloorY + 0.6, -depth * 0.22],
    processingPos: [width * 0.28, chamberFloorY + 0.5, -depth * 0.22],
    internalLifterHomePos: [lifterX * 0.5, chamberFloorY + 0.4, -depth * 0.10],
  }), [chamberFloorY, depth, lifterTopY, lifterX, lifterZ, width]);

  // Active state calculations
  const bigLifterPose = useMemo(() => {
    if (bigLifterEvent && bigLifterProgress01 != null) {
      return computeBigLifterPose(bigLifterProgress01, bigLifterEvent, stations);
    }
    return {
      carriageY: stations.transferPosY,
      holderEngaged: false,
      foupY: stations.transferPosY,
      foupLocation: 'EQUIPMENT_TRANSFER_POS' as const,
      state: 'IDLE' as const,
      topShuttleZ: 0.0,
      topHoistDropY: stations.trackPortY,
      topFoupVisible: false,
      topFoupY: stations.trackPortY,
      carriageFoupVisible: false,
    };
  }, [bigLifterEvent, bigLifterProgress01, stations]);


  // Refs for dynamic frame-driven animation
  const bigLifterCarriageRef = useRef<THREE.Group>(null);
  const topShuttleTrayRef = useRef<THREE.Group>(null);
  const topFoupRef = useRef<THREE.Group>(null);
  const carriageFoupRef = useRef<THREE.Group>(null);

  // Gentle animation and live event frame updates
  useFrame((state) => {
    if (cameraDragStore.isDragging()) return;
    // When idle and not transferring, skip per-frame calculations
    if (!bigLifterEvent && !animateLifter) {
      if (carriageFoupRef.current) {
        carriageFoupRef.current.visible = Boolean(transferFoupPresent);
      }
      return;
    }

    const t = state.clock.getElapsedTime();

    let activeCarriageY = stations.transferPosY;
    let activeShuttleZ = 0.0;
    let activeTopFoupVisible = false;
    let activeCarriageFoupVisible = false;

    // Big Lifter Vertical Carriage & Top Shuttle Tray (Strictly Event-Driven or continuous demo)
    if (bigLifterEvent && bigLifterProgress01 != null) {
      activeCarriageY = bigLifterPose.carriageY;
      activeShuttleZ = bigLifterPose.topShuttleZ;
      activeTopFoupVisible = bigLifterPose.topFoupVisible;
      activeCarriageFoupVisible = bigLifterPose.carriageFoupVisible;
    } else if (animateLifter && status === 'RUNNING') {
      const period = 10.0;
      const normT = (t % period) / period;
      const isPick = normT >= 0.5;
      const subT = isPick ? (normT - 0.5) * 2.0 : normT * 2.0;
      const demoPose = computeBigLifterPose(subT, isPick ? 'UP' : 'DOWN', stations);

      activeCarriageY = demoPose.carriageY;
      activeShuttleZ = demoPose.topShuttleZ;
      activeTopFoupVisible = demoPose.topFoupVisible;
      activeCarriageFoupVisible = demoPose.carriageFoupVisible;
    }

    if (bigLifterCarriageRef.current) {
      bigLifterCarriageRef.current.position.y = activeCarriageY;
    }
    if (topShuttleTrayRef.current) {
      topShuttleTrayRef.current.position.z = activeShuttleZ;
    }
    if (topFoupRef.current) {
      topFoupRef.current.visible = activeTopFoupVisible;
    }
    if (carriageFoupRef.current) {
      carriageFoupRef.current.visible = activeCarriageFoupVisible || Boolean(transferFoupPresent);
    }
  });

  return (
    <group
      name={`CleanroomWorkcell-${id}`}
      position={position}
      rotation={rotation}
      onClick={onSelect ? (e) => { e.stopPropagation(); onSelect(); } : undefined}
    >
      {/* ========================================================================= */}
      {/* 1. HEAVY-DUTY BASE SKID & LEVELING JACK FEET                              */}
      {/* ========================================================================= */}
      <mesh position={[0, 0.25, 0]} material={materials.darkChassis} receiveShadow>
        <boxGeometry args={[width + 0.2, 0.5, depth + 0.2]} />
      </mesh>

      {/* 4 Corner Leveling Jack Pads with blue/dark leveling feet */}
      {[-1, 1].flatMap((sx) =>
        [-1, 1].map((sz) => (
          <group
            key={`foot-${sx}-${sz}`}
            position={[(width * 0.5 - 0.8) * sx, 0, (depth * 0.5 - 0.8) * sz]}
          >
            {/* Jack pad square plate */}
            <mesh position={[0, 0.08, 0]} material={materials.silverAluminum} receiveShadow>
              <boxGeometry args={[1.2, 0.16, 1.2]} />
            </mesh>
            {/* Threaded steel leveling stem */}
            <mesh position={[0, 0.35, 0]} material={materials.brushedSteel}>
              <cylinderGeometry args={[0.2, 0.2, 0.5, 16]} />
            </mesh>
            {/* Caster mounting block */}
            <mesh position={[0, 0.7, 0]} material={materials.darkChassis}>
              <boxGeometry args={[0.8, 0.4, 0.8]} />
            </mesh>
          </group>
        ))
      )}

      {/* ========================================================================= */}
      {/* 2. LEFT CONTROL TOWER COLUMN (HMI, Buttons, Drawers, Latches)             */}
      {/* ========================================================================= */}
      <group position={[towerCenterX, 0, 0]}>
        {/* Main Solid White Tower Body */}
        <mesh position={[0, height * 0.5, 0]} material={materials.whiteChassis} castShadow receiveShadow>
          <boxGeometry args={[towerW, height, depth]} />
        </mesh>

        {/* Top Status Push Buttons & Toggle Switches */}
        <group position={[0, height * 0.88, depth * 0.5 + 0.02]}>
          {/* Bezel plate */}
          <mesh material={materials.silverAluminum}>
            <boxGeometry args={[towerW * 0.75, 1.2, 0.08]} />
          </mesh>
          {/* Green start button */}
          <mesh position={[-1.2, 0, 0.08]} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[0.22, 0.22, 0.12, 16]} />
            <meshStandardMaterial color="#10B981" emissive="#10B981" emissiveIntensity={0.8} />
          </mesh>
          {/* Amber toggle switch */}
          <mesh position={[-0.2, 0, 0.08]} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[0.18, 0.18, 0.12, 16]} />
            <meshStandardMaterial color="#F59E0B" emissive="#F59E0B" emissiveIntensity={0.8} />
          </mesh>
          {/* Red stop push button */}
          <mesh position={[0.8, 0, 0.08]} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[0.22, 0.22, 0.12, 16]} />
            <meshStandardMaterial color="#EF4444" emissive="#DC2626" emissiveIntensity={0.7} />
          </mesh>
        </group>

        {/* Touchscreen HMI Monitor */}
        <group position={[0, height * 0.70, depth * 0.5 + 0.02]}>
          {/* Outer Black Bezel Frame */}
          <mesh material={materials.darkChassis} castShadow>
            <boxGeometry args={[towerW * 0.88, height * 0.22, 0.12]} />
          </mesh>
          {/* Inner Screen Display */}
          <mesh position={[0, 0, 0.08]}>
            <planeGeometry args={[towerW * 0.82, height * 0.19]} />
            <meshBasicMaterial color="#0284C7" />
          </mesh>
          {/* HMI UI Header Bar */}
          <mesh position={[0, height * 0.075, 0.09]}>
            <planeGeometry args={[towerW * 0.78, 0.35]} />
            <meshBasicMaterial color="#0369A1" />
          </mesh>
          {/* Simulated HMI UI Data Grid Lines */}
          {[-0.8, 0, 0.8].map((lx) => (
            <mesh key={`hmi-col-${lx}`} position={[lx, -0.4, 0.09]}>
              <planeGeometry args={[0.9, 1.8]} />
              <meshBasicMaterial color="#0C4A6E" />
            </mesh>
          ))}
        </group>

        {/* Pull Drawer / Keyboard Tray with Chrome Handle */}
        <group position={[0, height * 0.52, depth * 0.5 + 0.02]}>
          {/* Drawer Face Panel */}
          <mesh material={materials.silverAluminum} castShadow>
            <boxGeometry args={[towerW * 0.88, height * 0.11, 0.1]} />
          </mesh>
          {/* Horizontal Chrome Bar Handle */}
          <mesh position={[0, 0, 0.14]} material={materials.chromeLatch} castShadow>
            <boxGeometry args={[towerW * 0.45, 0.16, 0.12]} />
          </mesh>
        </group>

        {/* Lower Access Cabinet Doors & Chrome Flush Latches */}
        <group position={[0, height * 0.24, depth * 0.5 + 0.02]}>
          {/* Left Door Seam */}
          <mesh position={[0, 0, 0.01]} material={materials.darkChassis}>
            <boxGeometry args={[0.04, height * 0.42, 0.02]} />
          </mesh>

          {/* Upper Chrome Flush Latches */}
          {[-towerW * 0.28, towerW * 0.28].map((lx, idx) => (
            <group key={`latch-upper-${idx}`} position={[lx, height * 0.12, 0.06]}>
              <mesh material={materials.darkChassis}>
                <boxGeometry args={[1.1, 0.7, 0.08]} />
              </mesh>
              <mesh position={[0, 0, 0.04]} material={materials.chromeLatch}>
                <boxGeometry args={[0.75, 0.4, 0.06]} />
              </mesh>
            </group>
          ))}

          {/* Vertical Digital Gauge & Inspection Slot */}
          <group position={[-towerW * 0.28, -height * 0.08, 0.06]}>
            <mesh material={materials.darkChassis}>
              <boxGeometry args={[0.6, 2.2, 0.08]} />
            </mesh>
            <mesh position={[0, 0.3, 0.04]}>
              <planeGeometry args={[0.42, 0.8]} />
              <meshBasicMaterial color="#22C55E" />
            </mesh>
            <mesh position={[0, -0.4, 0.04]}>
              <planeGeometry args={[0.42, 0.45]} />
              <meshBasicMaterial color="#00F0FF" />
            </mesh>
          </group>

          {/* Digital Readout Screen */}
          <group position={[towerW * 0.28, -height * 0.08, 0.06]}>
            <mesh material={materials.darkChassis}>
              <boxGeometry args={[1.2, 0.8, 0.08]} />
            </mesh>
            <mesh position={[0, 0, 0.04]}>
              <planeGeometry args={[0.95, 0.55]} />
              <meshBasicMaterial color="#00E5FF" />
            </mesh>
          </group>
        </group>

        {/* Emergency Stop Button (Yellow Bezel + Red Mushroom Cap) */}
        <group position={[towerW * 0.5 + 0.1, height * 0.48, depth * 0.5 - 0.2]} rotation={[0, 0, 0]}>
          <mesh material={materials.eStopYellow}>
            <cylinderGeometry args={[0.45, 0.45, 0.15, 24]} />
          </mesh>
          <mesh position={[0, 0, 0.18]} material={materials.eStopRed} castShadow>
            <cylinderGeometry args={[0.32, 0.36, 0.25, 24]} />
          </mesh>
        </group>

        {/* Multi-Tier Status Signal Tower Beacon on Top-Left Corner */}
        <group position={[-towerW * 0.35, height + 0.1, -depth * 0.35]}>
          {/* Aluminum mounting post */}
          <mesh position={[0, 0.8, 0]} material={materials.silverAluminum}>
            <cylinderGeometry args={[0.1, 0.1, 1.6, 16]} />
          </mesh>
          {/* Red section */}
          <mesh position={[0, 1.8, 0]}>
            <cylinderGeometry args={[0.26, 0.26, 0.3, 20]} />
            <meshStandardMaterial
              color={status === 'ERROR' || status === 'OFFLINE' ? '#EF4444' : '#330808'}
              emissive="#EF4444"
              emissiveIntensity={status === 'ERROR' || status === 'OFFLINE' ? 1.2 : 0.05}
            />
          </mesh>
          {/* Amber section */}
          <mesh position={[0, 2.15, 0]}>
            <cylinderGeometry args={[0.26, 0.26, 0.3, 20]} />
            <meshStandardMaterial
              color={status === 'IDLE' ? '#F59E0B' : '#332000'}
              emissive="#F59E0B"
              emissiveIntensity={status === 'IDLE' ? 1.2 : 0.05}
            />
          </mesh>
          {/* Green section */}
          <mesh position={[0, 2.5, 0]}>
            <cylinderGeometry args={[0.26, 0.26, 0.3, 20]} />
            <meshStandardMaterial
              color={status === 'RUNNING' ? '#10B981' : '#002208'}
              emissive="#10B981"
              emissiveIntensity={status === 'RUNNING' ? 1.2 : 0.05}
            />
          </mesh>
          {/* Cyan/Blue section */}
          <mesh position={[0, 2.85, 0]}>
            <cylinderGeometry args={[0.26, 0.26, 0.3, 20]} />
            <meshStandardMaterial
              color={status === 'RUNNING' ? '#06B6D4' : '#001822'}
              emissive="#06B6D4"
              emissiveIntensity={status === 'RUNNING' ? 1.2 : 0.05}
            />
          </mesh>
        </group>
      </group>

      {/* ========================================================================= */}
      {/* 3. LOWER CABINET UNDER WORKCELL CHAMBER (White Panels & Latches)          */}
      {/* ========================================================================= */}
      <group position={[chamberCenterX, baseH * 0.5, 0]}>
        {/* Main Solid White Lower Enclosure */}
        <mesh material={materials.whiteChassis} castShadow receiveShadow>
          <boxGeometry args={[chamberW, baseH, depth]} />
        </mesh>

        {/* Front Maintenance Access Doors & Chrome Flush Latches */}
        <group position={[0, 0, depth * 0.5 + 0.02]}>
          {[-chamberW * 0.24, chamberW * 0.24].map((lx, idx) => (
            <group key={`chamber-latch-${idx}`} position={[lx, baseH * 0.18, 0.04]}>
              <mesh material={materials.darkChassis}>
                <boxGeometry args={[1.2, 0.75, 0.08]} />
              </mesh>
              <mesh position={[0, 0, 0.04]} material={materials.chromeLatch}>
                <boxGeometry args={[0.8, 0.42, 0.06]} />
              </mesh>
            </group>
          ))}
          {/* Center Vertical Door Seam */}
          <mesh position={[0, 0, 0.01]} material={materials.darkChassis}>
            <boxGeometry args={[0.04, baseH * 0.85, 0.02]} />
          </mesh>
        </group>

        {/* Right Side Keyed Lock Access Panel */}
        <group position={[chamberW * 0.5 + 0.02, 0, 0]} rotation={[0, Math.PI / 2, 0]}>
          <mesh material={materials.darkChassis}>
            <boxGeometry args={[1.4, 2.2, 0.08]} />
          </mesh>
          <mesh position={[0, 0, 0.05]} material={materials.silverAluminum}>
            <boxGeometry args={[1.2, 2.0, 0.04]} />
          </mesh>
          <mesh position={[0.3, 0, 0.1]} material={materials.chromeLatch}>
            <cylinderGeometry args={[0.1, 0.1, 0.25, 16]} />
          </mesh>
        </group>
      </group>

      {/* ========================================================================= */}
      {/* 4. UPPER ROBOT PROCESS WORKCELL (Acrylic Glass & Aluminum Frame)          */}
      {/* ========================================================================= */}
      <group position={[chamberCenterX, 0, 0]}>
        {/* Perforated Stainless Steel Grid Floor Table */}
        <group position={[0, chamberFloorY + 0.15, 0]}>
          <mesh material={materials.brushedSteel} receiveShadow>
            <boxGeometry args={[chamberW - 0.2, 0.3, depth - 0.4]} />
          </mesh>
          {/* Perforated Airflow Hole Grid Patterns */}
          {[-2.5, 0, 2.5].map((gx) =>
            [-3, -1, 1, 3].map((gz) => (
              <mesh
                key={`grid-hole-${gx}-${gz}`}
                position={[gx, 0.16, gz]}
                material={materials.darkChassis}
              >
                <boxGeometry args={[1.8, 0.02, 1.2]} />
              </mesh>
            ))
          )}
        </group>

        {/* Rear Translucent Smoked Glass Window Panel */}
        <mesh
          position={[0, chamberCenterY, -depth * 0.5 + 0.1]}
          material={materials.smokedGlassBackdrop}
          castShadow={false}
        >
          <boxGeometry args={[chamberW - 0.4, chamberH - 0.4, 0.12]} />
        </mesh>

        {/* Rear Cleanroom Illuminator Light */}
        <pointLight
          position={[0, chamberCenterY, -depth * 0.3]}
          color="#F8FAFC"
          intensity={1.0}
          distance={16}
        />

        {/* Top Perforated HEPA Filter Fan Unit (FFU) Ceiling with Open Lifter Cutout */}
        {(() => {
          const lHalfW = 1.85;
          const lHalfD = 1.85;
          const holeHalfW = lHalfW + 0.08;
          const holeHalfD = lHalfD + 0.08;

          // Both this ceiling and Section 6 Lifter are children of <group position={[chamberCenterX, 0, 0]}>.
          // Therefore, the lifter position in this local coordinate space is exactly [lifterX, 0, lifterZ].
          const lifterRelX = lifterX;
          const lifterRelZ = stations.transferPos[2];
          const cutoutRearZ = lifterRelZ - holeHalfD;

          const rearDepth = Math.max(0.2, depth * 0.5 + cutoutRearZ);
          const rearCenterZ = -depth * 0.5 + rearDepth * 0.5;

          const frontDepth = Math.max(0.2, depth - rearDepth);
          const frontCenterZ = depth * 0.5 - frontDepth * 0.5;

          const wLeft = Math.max(0, (lifterRelX - holeHalfW) - (-chamberW * 0.5));
          const leftCenterX = -chamberW * 0.5 + wLeft * 0.5;

          const wRight = Math.max(0, chamberW * 0.5 - (lifterRelX + holeHalfW));
          const rightCenterX = chamberW * 0.5 - wRight * 0.5;

          return (
            <group position={[0, height - 0.4, 0]}>
              {/* 1. Solid Rear Ceiling Enclosure (Full width behind the front lifter) */}
              {rearDepth > 0.4 && (
                <group position={[0, 0, rearCenterZ]}>
                  <mesh material={materials.whiteChassis} castShadow receiveShadow>
                    <boxGeometry args={[chamberW, 0.8, rearDepth]} />
                  </mesh>
                  <mesh position={[0, -0.41, 0]} material={materials.silverAluminum}>
                    <boxGeometry args={[chamberW - 0.2, 0.04, rearDepth - 0.2]} />
                  </mesh>
                </group>
              )}

              {/* 2. Front-Left Ceiling Cap (Beside the lifter cutout on the left) */}
              {wLeft > 0.2 && frontDepth > 0.4 && (
                <group position={[leftCenterX, 0, frontCenterZ]}>
                  <mesh material={materials.whiteChassis} castShadow receiveShadow>
                    <boxGeometry args={[wLeft, 0.8, frontDepth]} />
                  </mesh>
                  <mesh position={[0, -0.41, 0]} material={materials.silverAluminum}>
                    <boxGeometry args={[wLeft - 0.1, 0.04, frontDepth - 0.2]} />
                  </mesh>
                </group>
              )}

              {/* 3. Front-Right Ceiling Cap (Beside the lifter cutout on the right) */}
              {wRight > 0.2 && frontDepth > 0.4 && (
                <group position={[rightCenterX, 0, frontCenterZ]}>
                  <mesh material={materials.whiteChassis} castShadow receiveShadow>
                    <boxGeometry args={[wRight, 0.8, frontDepth]} />
                  </mesh>
                  <mesh position={[0, -0.41, 0]} material={materials.silverAluminum}>
                    <boxGeometry args={[wRight - 0.1, 0.04, frontDepth - 0.2]} />
                  </mesh>
                </group>
              )}

              {/* Angled LED Work Light Bar Illuminating the Interior */}
              <group position={[0, -0.45, rearCenterZ]} rotation={[0.3, 0, 0]}>
                <mesh material={materials.silverAluminum}>
                  <boxGeometry args={[Math.max(1, chamberW * 0.7), 0.18, 0.4]} />
                </mesh>
                <mesh position={[0, -0.1, 0]} material={materials.ledLightBar}>
                  <boxGeometry args={[Math.max(0.8, chamberW * 0.65), 0.04, 0.32]} />
                </mesh>
                <pointLight
                  position={[0, -0.2, 0]}
                  intensity={2.2}
                  distance={18}
                  color="#FFFFFF"
                />
              </group>
            </group>
          );
        })()}


        {/* Aluminum Corner Frame Columns (Silver Anodized Structure) */}
        {[-1, 1].flatMap((sx) =>
          [-1, 1].map((sz) => (
            <mesh
              key={`chamber-col-${sx}-${sz}`}
              position={[
                sx * (chamberW * 0.5 - frameThick * 0.5),
                chamberCenterY,
                sz * (depth * 0.5 - frameThick * 0.5),
              ]}
              material={materials.silverAluminum}
              castShadow
              receiveShadow
            >
              <boxGeometry args={[frameThick, chamberH, frameThick]} />
            </mesh>
          ))
        )}

        {/* Top & Bottom Perimeter Silver Bezel Bars */}
        <mesh position={[0, chamberFloorY + frameThick * 0.5, depth * 0.5 - frameThick * 0.5]} material={materials.silverAluminum}>
          <boxGeometry args={[chamberW, frameThick, frameThick]} />
        </mesh>
        <mesh position={[0, height - frameThick * 0.5, depth * 0.5 - frameThick * 0.5]} material={materials.silverAluminum}>
          <boxGeometry args={[chamberW, frameThick, frameThick]} />
        </mesh>
        <mesh position={[chamberW * 0.5 - frameThick * 0.5, chamberFloorY + frameThick * 0.5, 0]} material={materials.silverAluminum}>
          <boxGeometry args={[frameThick, frameThick, depth - frameThick * 2]} />
        </mesh>
        <mesh position={[chamberW * 0.5 - frameThick * 0.5, height - frameThick * 0.5, 0]} material={materials.silverAluminum}>
          <boxGeometry args={[frameThick, frameThick, depth - frameThick * 2]} />
        </mesh>

        {/* Transparent Clear Glass / Acrylic Windows (Front & Right Faces) */}
        {/* Front Acrylic Window */}
        <mesh position={[0, chamberCenterY, depth * 0.5 - 0.05]} material={materials.clearAcrylic}>
          <boxGeometry args={[chamberW - frameThick * 2, chamberH - frameThick * 2, 0.08]} />
        </mesh>
        {/* Right Side Acrylic Window */}
        <mesh position={[chamberW * 0.5 - 0.05, chamberCenterY, 0]} material={materials.clearAcrylic}>
          <boxGeometry args={[0.08, chamberH - frameThick * 2, depth - frameThick * 2]} />
        </mesh>

        {/* 3-Tier Cleanroom Signal Tower Light (Green, Amber, Red on chrome mast - Matching Reference Image) */}
        <group position={[-chamberW * 0.42, height + 0.05, depth * 0.42]}>
          <mesh position={[0, 0.1, 0]} material={materials.silverAluminum}>
            <cylinderGeometry args={[0.32, 0.4, 0.2, 16]} />
          </mesh>
          <mesh position={[0, 0.75, 0]} material={materials.chromeLatch}>
            <cylinderGeometry args={[0.07, 0.07, 1.1, 16]} />
          </mesh>
          <mesh position={[0, 1.45, 0]} material={materials.towerGreen}>
            <cylinderGeometry args={[0.2, 0.2, 0.32, 16]} />
          </mesh>
          <mesh position={[0, 1.82, 0]} material={materials.towerAmber}>
            <cylinderGeometry args={[0.2, 0.2, 0.32, 16]} />
          </mesh>
          <mesh position={[0, 2.19, 0]} material={materials.towerRed}>
            <cylinderGeometry args={[0.2, 0.2, 0.32, 16]} />
          </mesh>
          <mesh position={[0, 2.4, 0]} material={materials.darkChassis}>
            <cylinderGeometry args={[0.22, 0.22, 0.1, 16]} />
          </mesh>
        </group>

        {/* Front Corner Emergency Stop Push Button */}
        <group position={[chamberW * 0.5 - 0.02, height * 0.52, depth * 0.5 + 0.15]}>
          <mesh material={materials.eStopYellow}>
            <cylinderGeometry args={[0.42, 0.42, 0.15, 24]} />
          </mesh>
          <mesh position={[0, 0, 0.16]} material={materials.eStopRed} castShadow>
            <cylinderGeometry args={[0.3, 0.34, 0.22, 24]} />
          </mesh>
        </group>

        {/* Top Illuminated Cleanroom Workcell Identification Badge */}
        <group position={[0, height + 0.35, depth * 0.5 + 0.08]}>
          <mesh position={[0, 0, 0]}>
            <boxGeometry args={[Math.min(chamberW * 0.8, 12.0), 0.8, 0.08]} />
            <meshStandardMaterial color="#0A101D" metalness={0.8} roughness={0.2} />
          </mesh>
          <mesh position={[0, 0, 0.03]}>
            <boxGeometry args={[Math.min(chamberW * 0.8 + 0.08, 12.1), 0.86, 0.02]} />
            <meshStandardMaterial
              color={status === 'RUNNING' ? '#10B981' : status === 'ERROR' ? '#EF4444' : '#00F0FF'}
              emissive={status === 'RUNNING' ? '#10B981' : status === 'ERROR' ? '#EF4444' : '#00F0FF'}
              emissiveIntensity={0.7}
            />
          </mesh>
          <mesh position={[0, 0, 0.05]}>
            <planeGeometry args={[Math.min(chamberW * 0.8, 12.0), 0.8]} />
            <meshBasicMaterial map={workcellSignTexture} transparent />
          </mesh>
        </group>

        {/* ========================================================================= */}
        {/* 6. BIG EXTERNAL LIFTER & EQUIPMENT TRANSFER POSITION ("Lifter")            */}
        {/* ========================================================================= */}
        {(() => {
          const lifterX = stations.transferPos[0];
          const lifterZ = stations.transferPos[2];
          const lifterTotalH = lifterTopY - chamberFloorY;
          const lifterCenterY = chamberFloorY + lifterTotalH * 0.5;
          const lColSize = 0.20;
          const lHalfW = 1.85;
          const lHalfD = 1.85;

          return (
            <group position={[lifterX, 0, lifterZ]}>
              {/* Lower Equipment Black Base Frame directly below vertical rods & columns */}
              <group position={[0, chamberFloorY + 0.08, 0]}>
                <mesh material={materials.darkChassis} castShadow receiveShadow>
                  <boxGeometry args={[lHalfW * 2 + 0.6, 0.16, lHalfD * 2 + 0.6]} />
                </mesh>
                {/* Corner mounting shoes for the 4 vertical columns */}
                {[-1, 1].flatMap((sx) =>
                  [-1, 1].map((sz) => (
                    <mesh
                      key={`rod-base-shoe-${sx}-${sz}`}
                      position={[sx * lHalfW, 0.14, sz * lHalfD]}
                      material={materials.darkChassis}
                      castShadow
                    >
                      <boxGeometry args={[lColSize + 0.14, 0.16, lColSize + 0.14]} />
                    </mesh>
                  ))
                )}
                {/* Center chrome rod mounting base shoes */}
                {[-1, 1].map((sx) => (
                  <mesh
                    key={`guide-base-shoe-${sx}`}
                    position={[sx * (lHalfW - 0.15), 0.14, 0]}
                    material={materials.darkChassis}
                    castShadow
                  >
                    <boxGeometry args={[0.26, 0.16, 0.26]} />
                  </mesh>
                ))}
              </group>

              {/* Lower Equipment Transfer Position Table Platform */}
              <mesh position={[0, chamberFloorY + 0.24, 0]} material={materials.silverAluminum} castShadow receiveShadow>
                <boxGeometry args={[lHalfW * 2 + 0.4, 0.24, lHalfD * 2 + 0.4]} />
              </mesh>

              {/* 4 Vertical Structural Guide Columns from Chamber Table to Overhead Track */}
              {[-1, 1].flatMap((sx) =>
                [-1, 1].map((sz) => (
                  <mesh
                    key={`lifter-col-${sx}-${sz}`}
                    position={[sx * lHalfW, lifterCenterY, sz * lHalfD]}
                    material={materials.silverAluminum}
                    castShadow
                    receiveShadow
                  >
                    <boxGeometry args={[lColSize, lifterTotalH, lColSize]} />
                  </mesh>
                ))
              )}

              {/* Transparent Clear Glass Lifter Chamber Windows (Left, Right, Front, Rear) */}
              {[-1, 1].map((sx) => (
                <mesh
                  key={`lifter-glass-x-${sx}`}
                  position={[sx * (lHalfW - 0.02), lifterCenterY, 0]}
                  material={materials.clearAcrylic}
                >
                  <boxGeometry args={[0.04, lifterTotalH - 0.2, lHalfD * 2 - lColSize]} />
                </mesh>
              ))}
              {[-1, 1].map((sz) => (
                <mesh
                  key={`lifter-glass-z-${sz}`}
                  position={[0, lifterCenterY, sz * (lHalfD - 0.02)]}
                  material={materials.clearAcrylic}
                >
                  <boxGeometry args={[lHalfW * 2 - lColSize, lifterTotalH - 0.2, 0.04]} />
                </mesh>
              ))}

              {/* Dual Polished Chrome Linear Guide Shafts */}
              {[-1, 1].map((sx) => (
                <mesh
                  key={`guide-shaft-${sx}`}
                  position={[sx * (lHalfW - 0.15), lifterCenterY - 1.5, 0]}
                  material={materials.chromeLatch}
                >
                  <cylinderGeometry args={[0.07, 0.07, Math.max(1.0, lifterTotalH - 3.0), 16]} />
                </mesh>
              ))}

              {/* Structural Cross-Tie Braces along the vertical mast (only between chamber ceiling and track) */}
              {[chamberFloorY + lifterTotalH * 0.35, chamberFloorY + lifterTotalH * 0.70].map((by, bIdx) => (
                <group key={`cross-tie-${bIdx}`} position={[0, by, 0]}>
                  <mesh position={[0, 0, -lHalfD]} material={materials.silverAluminum}>
                    <boxGeometry args={[lHalfW * 2 + lColSize, lColSize, lColSize]} />
                  </mesh>
                  <mesh position={[0, 0, lHalfD]} material={materials.silverAluminum}>
                    <boxGeometry args={[lHalfW * 2 + lColSize, lColSize, lColSize]} />
                  </mesh>
                </group>
              ))}

              {/* Ceiling Port Aperture Collar at Height Y = height (100% OPEN CENTER) */}
              <group position={[0, height, 0]}>
                {/* Left & Right border trims */}
                {[-1, 1].map((sx) => (
                  <mesh
                    key={`collar-side-${sx}`}
                    position={[sx * (lHalfW + 0.12), 0, 0]}
                    material={materials.darkChassis}
                  >
                    <boxGeometry args={[0.24, 0.3, lHalfD * 2 + 0.48]} />
                  </mesh>
                ))}
                {/* Front & Back border trims */}
                {[-1, 1].map((sz) => (
                  <mesh
                    key={`collar-fb-${sz}`}
                    position={[0, 0, sz * (lHalfD + 0.12)]}
                    material={materials.darkChassis}
                  >
                    <boxGeometry args={[lHalfW * 2, 0.3, 0.24]} />
                  </mesh>
                ))}
                {/* Cleanroom Optical Docking Proximity Sensors on outer rims */}
                {[-1, 1].map((sx) => (
                  <mesh key={`sensor-${sx}`} position={[sx * (lHalfW + 0.26), 0.2, 0]} material={materials.chromeLatch}>
                    <cylinderGeometry args={[0.08, 0.08, 0.2, 12]} />
                  </mesh>
                ))}
              </group>

              {/* Top Lifter Crown Frame (Capping Guide Columns at Y = lifterTopY matching track top edge) */}
              <group position={[0, lifterTopY, 0]}>
                {/* Left & Right Top Horizontal Perimeter Beams */}
                {[-1, 1].map((sx) => (
                  <mesh
                    key={`top-frame-lr-${sx}`}
                    position={[sx * lHalfW, 0, 0]}
                    material={materials.silverAluminum}
                    castShadow
                    receiveShadow
                  >
                    <boxGeometry args={[lColSize, 0.28, lHalfD * 2 + lColSize]} />
                  </mesh>
                ))}
                {/* Front & Back Top Horizontal Perimeter Beams */}
                {[-1, 1].map((sz) => (
                  <mesh
                    key={`top-frame-fb-${sz}`}
                    position={[0, 0, sz * lHalfD]}
                    material={materials.silverAluminum}
                    castShadow
                    receiveShadow
                  >
                    <boxGeometry args={[lHalfW * 2 + lColSize, 0.28, lColSize]} />
                  </mesh>
                ))}
                {/* 4 Corner Aluminum Column End Caps */}
                {[-1, 1].flatMap((sx) =>
                  [-1, 1].map((sz) => (
                    <mesh
                      key={`top-cap-${sx}-${sz}`}
                      position={[sx * lHalfW, 0.16, sz * lHalfD]}
                      material={materials.darkChassis}
                      castShadow
                    >
                      <boxGeometry args={[lColSize * 1.3, 0.08, lColSize * 1.3]} />
                    </mesh>
                  ))
                )}
                {/* Top Timing Belt Drive Pulley Housings */}
                {[-1, 1].map((sx) => (
                  <group key={`top-pulley-${sx}`} position={[sx * (lHalfW - 0.12), 0.2, 0]}>
                    <mesh material={materials.darkChassis} castShadow>
                      <boxGeometry args={[0.3, 0.28, 0.42]} />
                    </mesh>
                    <mesh position={[0, 0.16, 0]} material={materials.silverAluminum}>
                      <cylinderGeometry args={[0.1, 0.1, 0.08, 16]} />
                    </mesh>
                  </group>
                ))}
              </group>

              {/* ──────────────── Top Horizontal Linear Telescopic Track Extension Rails & Shuttle Tray ──────────────── */}
              <group position={[0, lifterTopY + 0.18, 0]}>
                {/* Dual Polished Chrome Slide Rails extending from lifter center (Z=0) towards track stop node (Z=+2.2) */}
                {[-1, 1].map((sx) => (
                  <mesh
                    key={`shuttle-rail-${sx}`}
                    position={[sx * (lHalfW * 0.72), 0, 1.05]}
                    material={materials.chromeLatch}
                  >
                    <boxGeometry args={[0.08, 0.08, 2.4]} />
                  </mesh>
                ))}
                {/* Structural Support Cross-Brackets connecting top frame to track extension */}
                {[0.2, 1.2, 2.1].map((bz, bIdx) => (
                  <mesh
                    key={`shuttle-brace-${bIdx}`}
                    position={[0, -0.06, bz]}
                    material={materials.darkChassis}
                  >
                    <boxGeometry args={[lHalfW * 1.6, 0.06, 0.14]} />
                  </mesh>
                ))}

                {/* ──────────────── Top Motorized Transfer Shuttle Tray (Collects/Deposits FOUP at OHT Stop Node) ──────────────── */}
                <group ref={topShuttleTrayRef} position={[0, 0.06, 0]}>
                  {/* Shuttle Base Slider Plate */}
                  <mesh material={materials.silverAluminum} castShadow receiveShadow>
                    <boxGeometry args={[lHalfW * 1.5, 0.12, lHalfD * 1.4]} />
                  </mesh>
                  {/* Linear Guide Slider Bushings */}
                  {[-1, 1].map((sx) => (
                    <mesh key={`shuttle-bushing-${sx}`} position={[sx * (lHalfW * 0.72), -0.04, 0]} material={materials.darkChassis}>
                      <boxGeometry args={[0.16, 0.14, 0.45]} />
                    </mesh>
                  ))}
                  {/* Safety Yellow Mechanical Clamping Cradle Brackets */}
                  {[-1, 1].map((sx) => (
                    <group key={`shuttle-cradle-${sx}`} position={[sx * (lHalfW * 0.58), 0.18, 0]}>
                      <mesh material={materials.safetyYellow} castShadow>
                        <boxGeometry args={[0.16, 0.24, 2.3]} />
                      </mesh>
                      {/* Flared Lead-In Centering Fingers */}
                      {[-1.0, 1.0].map((fz) => (
                        <mesh key={`shuttle-finger-${fz}`} position={[sx * -0.05, 0.08, fz]} material={materials.safetyYellow} castShadow>
                          <boxGeometry args={[0.10, 0.10, 0.24]} />
                        </mesh>
                      ))}
                    </group>
                  ))}
                  {/* Kinematic Nest Alignment Plate */}
                  <mesh position={[0, 0.08, 0]} material={materials.brushedSteel}>
                    <boxGeometry args={[2.5, 0.04, 2.5]} />
                  </mesh>
                  {/* Kinematic Alignment Pins */}
                  {[[0, 0.85], [-0.75, -0.55], [0.75, -0.55]].map(([px, pz], pIdx) => (
                    <mesh key={`shuttle-pin-${pIdx}`} position={[px, 0.14, pz]} material={materials.chromeLatch}>
                      <cylinderGeometry args={[0.06, 0.09, 0.10, 16]} />
                    </mesh>
                  ))}
                  {/* Active Laser Optical Proximity Alignment Sensors */}
                  {[-1, 1].map((sx) => (
                    <mesh key={`laser-sensor-${sx}`} position={[sx * (lHalfW * 0.65), 0.12, 1.1]} material={materials.verticalLedCyan}>
                      <cylinderGeometry args={[0.04, 0.04, 0.08, 12]} />
                    </mesh>
                  ))}

                  {/* 300mm FOUP on Top Shuttle Tray during extension / handoff */}
                  <group ref={topFoupRef} position={[0, 0.16, 0]} visible={Boolean(bigLifterPose.topFoupVisible || trackFoupPresent)}>
                    <FOUP scale={6.8} position={[0, 0, 0]} />
                  </group>
                </group>
              </group>

              {/* Moving Big Lifter Carriage Platform (Travels vertically along the Lifter Rods) */}
              <group ref={bigLifterCarriageRef} position={[0, stations.transferPosY, 0]}>
                {/* Carriage Base Frame */}
                <mesh material={materials.lifterCarriageFrame} castShadow receiveShadow>
                  <boxGeometry args={[lHalfW * 1.8, 0.28, lHalfD * 1.8]} />
                </mesh>
                {/* Linear Guide Bearing Slider Blocks */}
                {[-1, 1].map((sx) => (
                  <mesh key={`bearing-${sx}`} position={[sx * (lHalfW - 0.15), 0, 0]} material={materials.darkChassis}>
                    <boxGeometry args={[0.3, 0.45, 0.3]} />
                  </mesh>
                ))}
                {/* SEMI High-Visibility Safety-Yellow Motorized Clamping Cradle Brackets (Actuates during OHT handoff) */}
                {[-1, 1].map((sx) => (
                  <group
                    key={`carriage-cradle-arm-${sx}`}
                    position={[sx * (lHalfW * (bigLifterPose.holderEngaged ? 0.68 : 0.74)), 0.32, 0]}
                  >
                    <mesh material={materials.safetyYellow} castShadow>
                      <boxGeometry args={[0.22, 0.42, 2.6]} />
                    </mesh>
                    {/* Flared lead-in fingers */}
                    {[-1.2, 1.2].map((fz) => (
                      <mesh key={`cradle-finger-${fz}`} position={[sx * -0.06, 0.16, fz]} material={materials.safetyYellow} castShadow>
                        <boxGeometry args={[0.14, 0.16, 0.3]} />
                      </mesh>
                    ))}
                  </group>
                ))}
                {/* Rear Safety Yellow Stop Nest Bracket */}
                <mesh position={[0, 0.32, -1.25]} material={materials.safetyYellow} castShadow>
                  <boxGeometry args={[2.2, 0.38, 0.18]} />
                </mesh>
                {/* Precision Kinematic Docking Nest Plate */}
                <mesh position={[0, 0.18, 0]} material={materials.silverAluminum}>
                  <boxGeometry args={[3.2, 0.08, 3.2]} />
                </mesh>
                {/* 3 SEMI Kinematic Alignment Pins */}
                {[[0, 1.1], [-1.0, -0.7], [1.0, -0.7]].map(([px, pz], pIdx) => (
                  <mesh key={`pin-${pIdx}`} position={[px, 0.26, pz]} material={materials.chromeLatch}>
                    <cylinderGeometry args={[0.08, 0.12, 0.14, 16]} />
                  </mesh>
                ))}
                {/* Carriage Status LED Glow Edge */}
                <mesh position={[0, 0.15, lHalfD * 0.9]} rotation={[0, 0, 0]}>
                  <boxGeometry args={[lHalfW * 1.5, 0.06, 0.06]} />
                  <meshStandardMaterial
                    color={status === 'RUNNING' ? '#10B981' : '#F59E0B'}
                    emissive={status === 'RUNNING' ? '#10B981' : '#F59E0B'}
                    emissiveIntensity={1.4}
                  />
                </mesh>

                {/* 300mm FOUP on Big Lifter Carriage (during big lifter transfer or when resting at equipment transfer pos) */}
                <group
                  ref={carriageFoupRef}
                  position={[0, 0.26, 0]}
                  visible={Boolean(transferFoupPresent || bigLifterPose.foupLocation === 'BIG_LIFTER_CARRIAGE' || bigLifterPose.foupLocation === 'EQUIPMENT_TRANSFER_POS')}
                >
                  <FOUP scale={6.8} position={[0, 0, 0]} />
                </group>
              </group>
            </group>
          );
        })()}
      </group>
    </group>
  );
};

export default CleanroomWorkcell3D;


